import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';

import { optimizePath } from '@wetransfer/transfer/adsdk/helpers/optimize-path';
import { preloadImage } from '@wetransfer/transfer/adsdk/helpers/preload-image';
import { preloadVideo } from '@wetransfer/transfer/adsdk/helpers/preload-video';
import { TransferWallpaper } from '@wetransfer/transfer/adsdk/types';
import { WallpaperWrapper } from '@wetransfer/transfer/adsdk/wallpaper-wrapper';
import { usePrevious } from '@wetransfer/transfer/hooks/usePrevious';

import { MAX_TITLE_LENGTH } from './constants';
import { TransferWallpaperContainerProps } from './types';
import { useTimer } from './useTimer';

export default function TransferWallpaperContainer({
  wallpapers,
}: TransferWallpaperContainerProps) {
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const { timeLeft, restart } = useTimer();

  const [selectedWallpaper, setSelectedWallpaper] =
    useState<TransferWallpaper | null>(null);

  const getRandomWallpaper = useCallback(() => {
    const randomPosition = Math.floor(Math.random() * wallpapers.length);

    const randomWallpaper = wallpapers[randomPosition];

    return {
      key: Math.random().toString(36).substring(2),
      ...randomWallpaper,
    };
  }, [wallpapers]);

  const prevWallpapers = usePrevious<TransferWallpaper[] | undefined>(
    wallpapers
  );
  useEffect(() => {
    if (wallpapers.length === 0) {
      return;
    }

    const didWallpapersChange =
      wallpapers.map(({ src }) => src).join() !==
      (prevWallpapers || []).map(({ src }) => src).join();

    if ((timeLeft === 0 && wallpapers.length > 1) || didWallpapersChange) {
      setSelectedWallpaper(getRandomWallpaper());
      restart();
    }
  }, [getRandomWallpaper, timeLeft, wallpapers, prevWallpapers, restart]);

  useEffect(() => {
    if (wallpapers.length === 0) return;

    if (!selectedWallpaper) {
      setSelectedWallpaper(getRandomWallpaper());
    }
  }, [getRandomWallpaper, selectedWallpaper, wallpapers]);

  useEffect(() => {
    async function preloadImageSrc(src: string) {
      await preloadImage(src);

      setAssetsLoaded(true);
    }

    async function preloadVideoSrc(src: string) {
      await preloadVideo(src);

      setAssetsLoaded(true);
    }

    if (selectedWallpaper) {
      const format = selectedWallpaper.format;

      if (format === 'image') {
        void preloadImageSrc(optimizePath(selectedWallpaper));
      }

      if (format === 'video') {
        void preloadVideoSrc(selectedWallpaper.src);
      }
    }
  }, [selectedWallpaper]);

  const renderWallpaperLink = () => {
    const title = selectedWallpaper?.title;
    const href = selectedWallpaper?.href;

    const wallpaperTitle =
      title && title.length > MAX_TITLE_LENGTH
        ? title.slice(0, MAX_TITLE_LENGTH) + '...'
        : title;

    if (href && title) {
      return (
        <a
          href={href}
          rel="noopener noreferrer"
          className="wallpaper__title"
          target="_blank"
          title={title}
          data-cy="wallpaper-link"
          data-testid="wallpaper-link"
        >
          {wallpaperTitle}
        </a>
      );
    }

    if (title) {
      return (
        <span
          className="wallpaper__title"
          data-cy="wallpaper-title"
          data-testid="wallpaper-title"
          title={title}
        >
          {wallpaperTitle}
        </span>
      );
    }

    return null;
  };

  const handleClickOut = () => {
    window.open(selectedWallpaper?.href, '_self', 'noopener,noreferrer');
  };

  if (assetsLoaded && selectedWallpaper?.format === 'image') {
    return (
      <WallpaperWrapper creativeRandomKey={selectedWallpaper.key}>
        <div
          data-cy="wallpaper__image"
          data-testid="wallpaper__image"
          className={classnames(
            'wallpaper__image',
            selectedWallpaper?.href && 'wallpaper--is-clickable'
          )}
          onClick={selectedWallpaper?.href ? handleClickOut : undefined}
          role="presentation"
          style={{
            backgroundImage: `url(${optimizePath(selectedWallpaper)})`,
          }}
        />
        {renderWallpaperLink()}
      </WallpaperWrapper>
    );
  }

  if (assetsLoaded && selectedWallpaper?.format === 'video') {
    return (
      <WallpaperWrapper creativeRandomKey={selectedWallpaper.key}>
        <video
          data-testid="wallpaper__video"
          className={classnames(
            'wallpaper__video',
            selectedWallpaper?.href && 'wallpaper--is-clickable'
          )}
          onClick={selectedWallpaper?.href ? handleClickOut : undefined}
          src={selectedWallpaper?.src}
          autoPlay
          muted
          loop
        />
        {renderWallpaperLink()}
      </WallpaperWrapper>
    );
  }

  return null;
}
